import { HubApiError, setUnauthorizedHandler } from "@lifter/hub-ui/browser-shared";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCcw } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { App } from "../App";
import { useMeQuery } from "../api/me";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/ui/page-header";
import { usePortalPath } from "../routing/usePortalPath";
import { authClient } from "./auth-client";
import { useBrandedDocumentTitle } from "./instance-branding";
import { LoginPage } from "./LoginPage";
import { LOGIN_PATH } from "./login-path";

/**
 * Application gate. `GET /api/me` is the single authentication probe: a 401
 * renders the auth pages, success renders the portal. The auth routes
 * (`/login`) always renders its page — a signed-in user landing there is
 * bounced home by the page itself once `/api/me` resolves.
 */
export function AuthGate() {
  const { pathname, replace } = usePortalPath();
  const me = useMeQuery();

  const isSignedOut = me.isError && isUnauthenticated(me.error);

  // Signed-out users land on /login from any portal path. Rendering waits for
  // the redirect: flashing the portal skeleton at a signed-out user reads as
  // a broken page.
  const onAuthRoute = isAuthRoute(pathname);
  useEffect(() => {
    if (isSignedOut && !onAuthRoute) replace(LOGIN_PATH);
  }, [isSignedOut, onAuthRoute, replace]);

  if (onAuthRoute) return <LoginPage />;

  if (me.isPending) {
    return (
      <BootFrame>
        <Loader2
          aria-hidden="true"
          className="size-5 animate-spin text-[var(--color-text-tertiary)]"
        />
        <p className="text-sm text-[var(--color-text-secondary)]">Loading…</p>
      </BootFrame>
    );
  }

  if (me.isError) {
    if (isSignedOut) {
      // The redirect effect is about to land on /login.
      return null;
    }
    return <WorkspaceUnreachable onRetry={() => me.refetch()} />;
  }

  return (
    <UnauthorizedRedirect>
      <App />
    </UnauthorizedRedirect>
  );
}

function isAuthRoute(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === LOGIN_PATH;
}

function isUnauthenticated(error: unknown): boolean {
  return error instanceof HubApiError && error.status === 401;
}

/**
 * Wires the global 401 handler while the portal is mounted. A 401 on an
 * `/api/*` call means the session is no longer valid server-side: clear the
 * query cache so no stale authenticated data survives, revoke the session
 * best-effort (a dead session may already 401 the sign-out call itself), and
 * route to `/login`. The signed-out login page makes no authenticated API
 * calls, so this cannot loop — unlike a blind reload, which would re-trigger
 * the 401 on a persistently-rejecting backend.
 */
function UnauthorizedRedirect({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { navigate } = usePortalPath();

  useEffect(() => {
    return setUnauthorizedHandler(() => {
      queryClient.clear();
      void authClient.signOut().catch(() => undefined);
      navigate(LOGIN_PATH);
    });
  }, [queryClient, navigate]);

  return <>{children}</>;
}

/**
 * The boot failed before the portal could name the route, so this frame owns
 * the tab until it recovers — otherwise a branded instance sits on the static
 * vendor title for as long as the workspace stays unreachable.
 */
function WorkspaceUnreachable({ onRetry }: { onRetry(): void }) {
  useBrandedDocumentTitle("Can't reach this workspace");
  return (
    <BootFrame>
      <PageHeader
        subtitle="The workspace did not answer. Check your connection and try again."
        title="Can't reach this workspace"
      />
      <Button onClick={onRetry} variant="secondary">
        <RefreshCcw aria-hidden="true" className="size-4" />
        Retry
      </Button>
    </BootFrame>
  );
}

function BootFrame({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-[var(--color-page-bg)] px-6 text-center">
      {children}
    </main>
  );
}
